import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteBank, getBanks } from '../../../../api/endpoints/system';
import type { Bank } from '../../../../api/types/system';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { BankFormDialog } from '../BankFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/system.ts header). */
export default function BankListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Bank | null>(null);
  const [deleting, setDeleting] = useState<Bank | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['system', 'banks'], queryFn: () => getBanks() });
  const rows: Bank[] = query.data?.banks ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteBank(id),
    onSuccess: () => {
      enqueueSnackbar('Banka silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'banks'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Banka Ayarları</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Banka
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Banka Adı</TableCell>
              <TableCell>Pos Kodu</TableCell>
              <TableCell>Para Birimi</TableCell>
              <TableCell>Varsayılan</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı banka bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.bankName}</TableCell>
                <TableCell>{row.posCode ?? '—'}</TableCell>
                <TableCell>{row.currency ?? '—'}</TableCell>
                <TableCell>{row.isDefault ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.isActive ? 'Aktif' : 'Pasif'}
                    color={row.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BankFormDialog open={createOpen} bank={null} onClose={() => setCreateOpen(false)} />
      <BankFormDialog open={!!editing} bank={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Bankayı sil"
        description={`"${deleting?.bankName ?? ''}" bankasını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
